/*
 * Key log → what was actually typed.
 *
 * Ported from formatKeyLog / parseShortcut / formatKeyLogNodes in
 * frontend/app/components/activity/KeyLogTab.js. The first port of this page
 * rendered each entry as its own chip, which is not the feature: a column of
 * 400 chips reading "a" "d" "m" "i" "n" is unreadable, and the whole point is
 * to see the sentence.
 *
 * TWO WIRE FORMATS, BOTH STILL IN THE DATABASE.
 *
 *   old (pre-2.0)  raw key NAMES from the OS: "LEFT SHIFT", "PERIOD",
 *                  "NUMPAD 5", "A". Shift is a separate entry and the reader
 *                  has to apply it.
 *   new            the desktop resolves the character before upload: "a", "@",
 *                  " ", plus bracketed labels for the rest — "[BACKSPACE]",
 *                  "[ENTER]", "[CMD+C]".
 *
 * Both are decoded here, because a user's history spans the change and a
 * viewer that only understands one of them shows a blank panel for the other.
 *
 * WHAT THIS MEANS FOR PRIVACY. Reconstruction is possible because the capture
 * really is character-level: "admin123" comes back as "admin123", passwords
 * included. The UI must not claim otherwise — see the note in KeyLogList.
 */

/* ---- Old-format key names ------------------------------------------------ */

const SHIFT_KEYS = new Set(["LEFT SHIFT", "RIGHT SHIFT"]);

// Pressed, but they type nothing. They also CLEAR a pending shift, so that a
// stray modifier does not leak into the next character.
const IGNORE_KEYS = new Set([
    "LEFT CTRL", "RIGHT CTRL", "LEFT ALT", "RIGHT ALT", "LEFT META", "RIGHT META",
    "CAPS LOCK", "ESCAPE", "TAB", "INSERT", "DELETE", "HOME", "END",
    "PAGE UP", "PAGE DOWN", "UP ARROW", "DOWN ARROW", "LEFT ARROW", "RIGHT ARROW",
    "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11", "F12",
    "PRINT SCREEN", "SCROLL LOCK", "PAUSE",
]);

const NORMAL_MAP = {
    SPACE: " ", RETURN: "\n",
    PERIOD: ".", DOT: ".", COMMA: ",", "FORWARD SLASH": "/", SEMICOLON: ";", QUOTE: "'",
    MINUS: "-", EQUALS: "=",
    "SQUARE BRACKET CLOSE": "[", "SQUARE BRACKET OPEN": "]",
    "LEFT BRACKET": "[", "RIGHT BRACKET": "]",
    BACKSLASH: "\\", GRAVE: "`", BACKTICK: "`",
    0: "0", 1: "1", 2: "2", 3: "3", 4: "4", 5: "5", 6: "6", 7: "7", 8: "8", 9: "9",
    "NUMPAD 0": "0", "NUMPAD 1": "1", "NUMPAD 2": "2", "NUMPAD 3": "3", "NUMPAD 4": "4",
    "NUMPAD 5": "5", "NUMPAD 6": "6", "NUMPAD 7": "7", "NUMPAD 8": "8", "NUMPAD 9": "9",
    "NUMPAD PERIOD": ".", "NUMPAD SLASH": "/", "NUMPAD MULTIPLY": "*",
    "NUMPAD MINUS": "-", "NUMPAD PLUS": "+",
};

const SHIFTED_MAP = {
    SPACE: " ", RETURN: "\n",
    "FORWARD SLASH": "?", PERIOD: ">", DOT: ">", COMMA: "<", SEMICOLON: ":", QUOTE: '"',
    MINUS: "_", EQUALS: "+",
    "SQUARE BRACKET CLOSE": "{", "SQUARE BRACKET OPEN": "}",
    "LEFT BRACKET": "{", "RIGHT BRACKET": "}",
    BACKSLASH: "|", GRAVE: "~", BACKTICK: "~",
    1: "!", 2: "@", 3: "#", 4: "$", 5: "%", 6: "^", 7: "&", 8: "*", 9: "(", 0: ")",
};

/*
 * WHICH FORMAT IS THIS LOG IN?
 *
 * The two overlap on single characters: "C" is a capital C in the new format
 * and a lower-case c in the old one (where "LEFT SHIFT" would precede it to
 * make it a capital). Nothing marks the format on the record, so it is decided
 * per log by looking for entries only the old encoder produced — a modifier, an
 * ignored key, or a spelled-out name like SPACE or PERIOD.
 *
 * This matters for real data: of 936 stored key logs in the reference database,
 * 203 are the old format. The original implementation checked `key.length === 1`
 * BEFORE its own `/^[A-Z]$/` branch, which made that branch unreachable — so
 * every old log rendered in capitals ("C" instead of "c") and every shifted
 * digit came out unshifted ("2" instead of "@"). The dead branch and its comment
 * show that was not the intent.
 */
function isOldFormat(keyLog) {
    for (const entry of keyLog) {
        const key = typeof entry === "string" ? entry : (entry?.key ?? "");
        if (key.length <= 1) continue;
        if (SHIFT_KEYS.has(key) || IGNORE_KEYS.has(key)) return true;
        if (Object.hasOwn(NORMAL_MAP, key)) return true;
    }
    return false;
}

/*
 * Control characters are not typed text. They turn up in the stream from
 * mis-mapped keys — the reference database has records that are nothing but
 * U+001F — and appended verbatim they are invisible: the panel looks empty
 * while claiming to have content. They are dropped from the reconstruction and
 * still shown in the raw view, where "what was actually recorded" is the point.
 *
 * Carriage return is the exception: it is Enter, and dropping it would join two
 * typed lines into one.
 */
const isControlChar = (key) =>
    key.length === 1 && key !== "\n" && key !== "\t" && key !== "\r" && key < " ";

/**
 * The typed text, as a plain string.
 *
 * Backspace really deletes — the text is rebuilt as it was written, not as it
 * was pressed, so a corrected typo reads as the correction rather than as both
 * attempts.
 */
export function formatKeyLog(keyLog = []) {
    let text = "";
    let shiftActive = false;
    const old = isOldFormat(keyLog);

    for (const entry of keyLog) {
        const key = typeof entry === "string" ? entry : (entry?.key ?? "");
        if (!key) continue;

        // -- new format: bracketed labels --
        if (key === "[BACKSPACE]" || key === "BACKSPACE") {
            text = text.slice(0, -1);
            shiftActive = false;
            continue;
        }
        if (key === "[ENTER]") {
            text += "\n";
            shiftActive = false;
            continue;
        }
        if (key === "[TAB]") {
            text += "\t";
            shiftActive = false;
            continue;
        }
        // Any other bracketed label — a shortcut, an arrow, a function key —
        // types nothing. formatKeyLogNodes() surfaces the shortcuts separately.
        if (key.startsWith("[") && key.endsWith("]")) {
            shiftActive = false;
            continue;
        }

        // -- old format: modifiers --
        if (SHIFT_KEYS.has(key)) {
            shiftActive = true;
            continue;
        }
        if (IGNORE_KEYS.has(key)) {
            shiftActive = false;
            continue;
        }

        // -- old format: a letter, cased by the pending shift --
        if (old && /^[A-Z]$/.test(key)) {
            text += shiftActive ? key : key.toLowerCase();
            shiftActive = false;
            continue;
        }

        // -- old format: a digit, which shift turns into a symbol --
        if (old && shiftActive && SHIFTED_MAP[key] !== undefined) {
            text += SHIFTED_MAP[key];
            shiftActive = false;
            continue;
        }

        // -- new format: the character, already resolved by the desktop --
        if (key.length === 1) {
            // A lone carriage return is Enter; normalised so the two paths that
            // produce a line break agree.
            if (key === "\r") text += "\n";
            else if (!isControlChar(key)) text += key;
            shiftActive = false;
            continue;
        }

        // -- old format: a named key --
        const map = shiftActive ? SHIFTED_MAP : NORMAL_MAP;
        if (map[key] !== undefined) text += map[key];
        shiftActive = false;
    }

    return text;
}

/* ---- Shortcuts ----------------------------------------------------------- */

const SHORTCUT_MOD_SYMBOLS = { CMD: "⌘", META: "⌘", CTRL: "Ctrl", ALT: "⌥", SHIFT: "⇧" };
const SHORTCUT_MODS = new Set(["CMD", "META", "CTRL", "ALT", "SHIFT"]);

// Named because "⌘+C" tells you which keys; "Copy" tells you what happened,
// and it is what someone reviewing a session is actually looking for.
const SHORTCUT_ACTIONS = {
    C: "Copy", V: "Paste", X: "Cut", A: "Select All", Z: "Undo", Y: "Redo",
    S: "Save", F: "Find", P: "Print", N: "New", W: "Close", T: "New Tab",
    Q: "Quit", R: "Reload",
};

/**
 * "[CMD+C]" → { display: "⌘+C", label: "Copy" }, or null when the entry is not
 * a shortcut.
 *
 * Shift alone does not make one: "[SHIFT+A]" is a capital A, not a command.
 */
export function parseShortcut(key) {
    if (typeof key !== "string" || !key.startsWith("[") || !key.endsWith("]")) return null;

    const parts = key.slice(1, -1).split("+");
    if (parts.length < 2) return null;

    const mods = parts.slice(0, -1);
    const mainKey = parts[parts.length - 1];

    if (!mods.every((m) => SHORTCUT_MODS.has(m))) return null;
    if (!mods.some((m) => m === "CMD" || m === "META" || m === "CTRL" || m === "ALT")) return null;

    const display = [...mods.map((m) => SHORTCUT_MOD_SYMBOLS[m] ?? m), mainKey].join("+");

    let label = SHORTCUT_ACTIONS[mainKey] ?? "";
    if (mainKey === "Z" && mods.includes("SHIFT")) label = "Redo";

    return { display, label };
}

/**
 * The typed text with the shortcuts left in place, as a node list.
 *
 * A run of characters becomes one text node; a shortcut becomes its own node so
 * the renderer can draw it as a chip. Without this a copy/paste is invisible in
 * the reconstruction — the text simply jumps, and nothing says why.
 */
export function formatKeyLogNodes(keyLog = []) {
    const nodes = [];
    let segment = [];

    const flush = () => {
        if (!segment.length) return;
        const text = formatKeyLog(segment);
        if (text) nodes.push({ type: "text", value: text });
        segment = [];
    };

    for (const entry of keyLog) {
        const key = typeof entry === "string" ? entry : (entry?.key ?? "");
        const shortcut = parseShortcut(key);

        if (shortcut) {
            flush();
            nodes.push({ type: "shortcut", ...shortcut });
        } else {
            segment.push(key);
        }
    }

    flush();
    return nodes;
}

/**
 * A one-line preview, capped, for a table cell.
 *
 * Newlines become spaces — a cell is one line, and a reconstruction full of
 * them would otherwise silently lose everything after the first.
 */
export function previewNodes(keyLog = [], max = 120) {
    const nodes = formatKeyLogNodes(keyLog);
    const out = [];
    let used = 0;

    for (const node of nodes) {
        if (used >= max) return { nodes: out, truncated: true };

        if (node.type === "text") {
            let text = node.value.replace(/\n/g, " ");
            const truncated = used + text.length > max;
            if (truncated) text = text.slice(0, Math.max(0, max - used));
            used += text.length;
            if (text) out.push({ type: "text", value: text });
            if (truncated) return { nodes: out, truncated: true };
        } else {
            out.push(node);
            used += node.display.length + (node.label ? node.label.length + 1 : 0);
        }
    }

    return { nodes: out, truncated: false };
}

/*
 * macOS sends its arrow keys as control characters — 0x1C-0x1F — and the
 * desktop passes them through. They are invisible in a chip, so the raw view
 * names them; the reconstruction drops them, since an arrow types nothing.
 */
const CONTROL_NAMES = {
    "\u001c": "←",
    "\u001d": "→",
    "\u001e": "↑",
    "\u001f": "↓",
};

/** How a raw entry should read as a chip, and which kind it is. */
export function describeKey(entry) {
    const key = typeof entry === "string" ? entry : (entry?.key ?? "");
    const shortcut = parseShortcut(key);
    if (shortcut) return { kind: "shortcut", ...shortcut };

    if (key === " ") return { kind: "special", display: "SPACE" };
    if (key === "\n" || key === "\r") return { kind: "special", display: "ENTER" };
    if (CONTROL_NAMES[key]) return { kind: "special", display: CONTROL_NAMES[key] };
    if (isControlChar(key)) return { kind: "special", display: `U+${key.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0")}` };
    if (key.startsWith("[") && key.endsWith("]")) return { kind: "special", display: key };
    if (SHIFT_KEYS.has(key)) return { kind: "modifier", display: key };
    if (key.length === 1) return { kind: "printable", display: key };

    return { kind: "named", display: key };
}
